import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressBadgesV3BadgesServiceV3Url(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www.wixapis.com': [
      {
        srcPath: '/members/v3/badges',
        destPath: '/v3/badges',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/members-badges-server/v3',
        destPath: '/v3',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_members_badges';

/**
 * Creates a badge.
 *
 * New badges do not have any badge permissions by default. Wix users can set badge permissions from the [Badges page in the dashboard](https://support.wix.com/en/article/site-members-creating-and-managing-member-badges?tabs=Dashboard-0#managing-member-badges).
 *
 * If `backgroundColor` or `textColor` are not specified, they default to `"#796EFF"` (purple) and `"#FFFFFF"` (white) respectively.
 */
export function createBadge(payload: object): RequestOptionsFactory<any> {
  function __createBadge({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'badge.createdDate' }, { path: 'badge.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.badges.v3.badge',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.badges.v3.BadgesServiceV3.CreateBadge',
      packageName: PACKAGE_NAME,
      url: resolveComWixpressBadgesV3BadgesServiceV3Url({
        protoPath: '/v3/badges',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'badge.createdDate' },
              { path: 'badge.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createBadge;
}

/**
 * Updates a badge.
 *
 * Note that the badge `slug` is based on the badge `title`. If you update the `title`, the `slug` updates accordingly.
 */
export function updateBadge(payload: object): RequestOptionsFactory<any> {
  function __updateBadge({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'badge.createdDate' }, { path: 'badge.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.badges.v3.badge',
      method: 'PATCH' as any,
      methodFqn: 'com.wixpress.badges.v3.BadgesServiceV3.UpdateBadge',
      packageName: PACKAGE_NAME,
      url: resolveComWixpressBadgesV3BadgesServiceV3Url({
        protoPath: '/v3/badges/{badge.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'badge.createdDate' },
              { path: 'badge.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateBadge;
}

/**
 * Retrieves up to 1,000 badges, given the requested paging.
 *
 * Default `paging.limit` is `100` and `paging.offset` is `0`.
 *
 * This method is not universal and only runs on the backend.
 */
export function listBadges(payload: object): RequestOptionsFactory<any> {
  function __listBadges({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.badges.v3.badge',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.badges.v3.BadgesServiceV3.ListBadges',
      packageName: PACKAGE_NAME,
      url: resolveComWixpressBadgesV3BadgesServiceV3Url({
        protoPath: '/v3/badges',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'badges.createdDate' },
              { path: 'badges.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listBadges;
}

/**
 * Retrieves a badge.
 *
 * This method is not universal and only runs on the backend.
 */
export function getBadge(payload: object): RequestOptionsFactory<any> {
  function __getBadge({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.badges.v3.badge',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.badges.v3.BadgesServiceV3.GetBadge',
      packageName: PACKAGE_NAME,
      url: resolveComWixpressBadgesV3BadgesServiceV3Url({
        protoPath: '/v3/badges/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'badge.createdDate' },
              { path: 'badge.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getBadge;
}

/** Deletes a badge. */
export function deleteBadge(payload: object): RequestOptionsFactory<any> {
  function __deleteBadge({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.badges.v3.badge',
      method: 'DELETE' as any,
      methodFqn: 'com.wixpress.badges.v3.BadgesServiceV3.DeleteBadge',
      packageName: PACKAGE_NAME,
      url: resolveComWixpressBadgesV3BadgesServiceV3Url({
        protoPath: '/v3/badges/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteBadge;
}

/**
 * Assigns a badge to site members.
 *
 * Members inherit the badge's permissions when they receive the badge.
 * Badge permissions are added to previous member permissions, they don't replace the existing permissions.
 */
export function assignBadge(payload: object): RequestOptionsFactory<any> {
  function __assignBadge({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.badges.v3.badge',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.badges.v3.BadgesServiceV3.AssignBadge',
      packageName: PACKAGE_NAME,
      url: resolveComWixpressBadgesV3BadgesServiceV3Url({
        protoPath: '/v3/badges/{id}/members',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __assignBadge;
}

/**
 * Removes an assigned badge from multiple site members.
 *
 * The members lose the badge's permissions when the badge is removed.
 */
export function unassignBadge(payload: object): RequestOptionsFactory<any> {
  function __unassignBadge({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.badges.v3.badge',
      method: 'DELETE' as any,
      methodFqn: 'com.wixpress.badges.v3.BadgesServiceV3.UnassignBadge',
      packageName: PACKAGE_NAME,
      url: resolveComWixpressBadgesV3BadgesServiceV3Url({
        protoPath: '/v3/badges/{id}/members',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __unassignBadge;
}

/**
 * Retrieves up to 1,000 site members assigned to a badge.
 *
 * Default `paging.limit` is `100` and `paging.offset` is `0`.
 *
 * This method is not universal and only runs on the backend.
 */
export function listMembers(payload: object): RequestOptionsFactory<any> {
  function __listMembers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.badges.v3.badge',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.badges.v3.BadgesServiceV3.ListMembers',
      packageName: PACKAGE_NAME,
      url: resolveComWixpressBadgesV3BadgesServiceV3Url({
        protoPath: '/v3/badges/{id}/members',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __listMembers;
}

/**
 * Retrieves badges assigned to specified members.
 *
 * Members are typically associated with a contact, each having a distinct `memberId` and `contactId`. When passing the ID as a parameter, avoid presuming the IDs are identical since they represent separate entities.
 *
 * This method is not universal and only runs on the backend.
 */
export function listMembersBadgeIds(
  payload: object
): RequestOptionsFactory<any> {
  function __listMembersBadgeIds({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.badges.v3.badge',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.badges.v3.BadgesServiceV3.ListMembersBadgeIds',
      packageName: PACKAGE_NAME,
      url: resolveComWixpressBadgesV3BadgesServiceV3Url({
        protoPath: '/v3/badges/members',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __listMembersBadgeIds;
}

/**
 * Retrieves a member count per badge.
 *
 * This method is not universal function and only runs on the backend.
 */
export function getMemberCountsPerBadge(
  payload: object
): RequestOptionsFactory<any> {
  function __getMemberCountsPerBadge({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.badges.v3.badge',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.badges.v3.BadgesServiceV3.GetMemberCountsPerBadge',
      packageName: PACKAGE_NAME,
      url: resolveComWixpressBadgesV3BadgesServiceV3Url({
        protoPath: '/v3/badges/members/count',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getMemberCountsPerBadge;
}

/**
 * Updates the display order of badges.
 *
 * This method is not universal function and only runs on the backend
 */
export function updateBadgesDisplayOrder(
  payload: object
): RequestOptionsFactory<any> {
  function __updateBadgesDisplayOrder({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.badges.v3.badge',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.badges.v3.BadgesServiceV3.UpdateBadgesDisplayOrder',
      packageName: PACKAGE_NAME,
      url: resolveComWixpressBadgesV3BadgesServiceV3Url({
        protoPath: '/v3/badges/order',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'badges.createdDate' },
              { path: 'badges.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateBadgesDisplayOrder;
}
